/**
 * Timezone-neutral calendar date helpers.
 * Use for holiday blackouts and date-only pickers so the stored YYYY-MM-DD
 * matches the calendar day the user selected (no UTC midnight shift).
 */

export const formatCalendarDateString = date => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const parseCalendarDateString = dateStr => {
  if (!dateStr || typeof dateStr !== "string") {
    return null
  }
  const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) {
    return null
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null
  }
  return new Date(year, month - 1, day)
}

export const parseCalendarDateStrings = (dates = []) =>
  (Array.isArray(dates) ? dates : [])
    .map(parseCalendarDateString)
    .filter(Boolean)

/** Start of today in local time (for calendar-date comparisons). */
export const getStartOfToday = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

/** True when YYYY-MM-DD is today or a future calendar day. */
export const isCalendarDateTodayOrFuture = dateStr => {
  const parsed = parseCalendarDateString(dateStr)
  if (!parsed) {
    return false
  }
  return parsed.getTime() >= getStartOfToday().getTime()
}

/** Keep only today/future YYYY-MM-DD values (drops past blackout dates). */
export const filterTodayOrFutureCalendarDates = (dates = []) => {
  const startOfToday = getStartOfToday().getTime()
  return (Array.isArray(dates) ? dates : [])
    .map(value => {
      if (typeof value === "string") {
        return value.trim()
      }
      return formatCalendarDateString(value)
    })
    .filter(value => {
      if (!value) {
        return false
      }
      const parsed = parseCalendarDateString(value)
      return parsed && parsed.getTime() >= startOfToday
    })
}

export const formatCalendarDateRangeForFilter = dates => {
  const formattedDates = dates
    .map(formatCalendarDateString)
    .filter(Boolean)

  if (formattedDates.length < 2) {
    return null
  }

  return {
    min: `${formattedDates[0]} 00:00`,
    max: `${formattedDates[1]} 23:59`,
  }
}
